import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import '../styles/Landing.css';
import aeroHubImage from '../assets/images/aero-hub-image.jpg';
import emailjs from 'emailjs-com';

const Landing = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });
  const formRef = useRef();

  const openVideoModal = (videoSrc) => {
    // Create a modal and play the video
    const modal = document.createElement('div');
    modal.className = 'video-modal';
    
    const videoContainer = document.createElement('div');
    videoContainer.className = 'modal-video-container';
    
    const video = document.createElement('video');
    video.controls = true;
    video.autoplay = true;
    video.src = process.env.PUBLIC_URL + videoSrc;
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'modal-close-btn';
    closeBtn.innerHTML = '×';
    closeBtn.onclick = () => document.body.removeChild(modal);
    
    videoContainer.appendChild(video);
    videoContainer.appendChild(closeBtn);
    modal.appendChild(videoContainer);
    
    document.body.appendChild(modal);
    
    // Close modal when clicking outside the video
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  };

  // Function to open WhatsApp with the given number
  const openWhatsApp = () => {
    // WhatsApp URL with phone number
    const whatsappUrl = `https://wa.me/60162820967`;
    // Open in a new tab
    window.open(whatsappUrl, '_blank');
  };

  // Function to handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    // Replace these with your actual EmailJS credentials
    const serviceId = 'service_muv1ab9';
    const templateId = 'template_fwc7m4c';
    const userId = 'utUIs2-NuReYS-2ZE';

    emailjs.sendForm(serviceId, templateId, formRef.current, userId)
      .then((result) => {
        console.log('Email sent successfully:', result.text);
        setSubmitStatus({
          success: true,
          message: 'Thank you for your message! We will get back to you soon.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        setTimeout(() => {
          setShowContactModal(false);
          setSubmitStatus({ success: false, message: '' });
        }, 3000);
      })
      .catch((error) => {
        console.error('Error sending email:', error.text);
        setSubmitStatus({
          success: false,
          message: 'Failed to send your message. Please try again later.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="landing-page">
      {/* Contact Form Modal */}
      {showContactModal && (
        <div className="modal-backdrop">
          <div className="contact-modal">
            <div className="modal-header">
              <h3>Contact Us</h3>
              <button 
                className="close-button" 
                onClick={() => setShowContactModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {submitStatus.message && (
                <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'}`}>
                  {submitStatus.message}
                </div>
              )}
              <form ref={formRef} onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="4"
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <section className="hero-section" style={{ 
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${aeroHubImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container">
          <div className="hero-content">
            <h1 className="welcome-title">Welcome to <span className="brand-name">Aero Industrial Hub</span></h1>
            <p className="subtitle">Your premier destination for industrial property management</p>
            <div className="cta-buttons">
              <Link to="/properties" className="btn btn-primary">View Properties</Link>
              <a href="https://aeroindustrialhub.com/login" className="btn btn-secondary" target="_blank" rel="noopener noreferrer">Sign Up Today</a>
            </div>
          </div>
        </div>
      </section>
      
      <section className="about-section">
        <div className="container">
          <h2 className="section-title">About Aero Industrial Hub</h2>
          <div className="about-content">
            <div className="about-image">
              <img 
                src={aeroHubImage}
                alt="Aero Industrial Hub" 
              />
            </div>
            <div className="about-text-wrapper">
              <div className="about-text">
                <p className="about-paragraph">
                  <span className="company-name">AeroIndustrialHub</span> is Malaysia's premier industrial, connecting businesses with high-quality industrial spaces tailored to their specific needs.
                </p>
                <p>Our comprehensive approach ensures that each property meets the highest standards of quality, strategic location, and future growth potential.</p>
                <p>With our expert team and verified partners, we simplify the process of finding, leasing, or purchasing industrial properties, making it easier for businesses to establish and expand their operations.</p>
                <Link to="/aero-industrial-hub" className="btn btn-outline">Learn More</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Transform Your Business Future Section */}
      <section className="transform-section">
        <div className="container">
          <h2 className="section-title">Transform Your Business Future</h2>
          <div className="transform-content">
            <div className="transform-text">
              <p>
                AERO Industrial Hub - Nilai's Premier Industrial Space offers cutting-edge 
                industrial lots at the strategic core of Nilai 3 Industrial Park. Positioned along 
                major road frontage, this development capitalizes on Nilai's expanding industrial 
                ecosystem. Our state-of-the-art facilities blend advanced infrastructure with 
                operational efficiency, perfectly suited for modern manufacturing and logistics 
                operations.
              </p>
              <Link to="/aero-industrial-hub" className="btn btn-outline">Explore Opportunities</Link>
            </div>
            <div className="transform-image">
              <img 
                src={process.env.PUBLIC_URL + '/images/aero-hub-image2.jpg'} 
                alt="AERO Industrial Hub" 
              />
            </div>
          </div>
        </div>
      </section>
      
      <section className="video-section">
        <div className="container">
          <h2 className="section-title">Take a Virtual Tour</h2>
          <div className="video-container">
            <div className="video-options">
              <div className="fallback-video">
                <video 
                  src={process.env.PUBLIC_URL + '/videos/property-showcase.mp4'}
                  className="video-placeholder-img" 
                  muted
                  preload="metadata"
                />
                <div className="video-overlay">
                  <div className="video-title">Property Tour</div>
                  <button className="video-play-button" onClick={() => openVideoModal('/videos/property-showcase.mp4')}>
                    <span className="play-icon">▶</span>
                    <span className="play-text">Watch Video 1</span>
                  </button>
                </div>
              </div>
              
              <div className="fallback-video">
                <video 
                  src={process.env.PUBLIC_URL + '/videos/property-showcase2.mp4'}
                  className="video-placeholder-img" 
                  muted
                  preload="metadata"
                />
                <div className="video-overlay">
                  <div className="video-title">Interior Features</div>
                  <button className="video-play-button" onClick={() => openVideoModal('/videos/property-showcase2.mp4')}>
                    <span className="play-icon">▶</span>
                    <span className="play-text">Watch Video 2</span>
                  </button>
                </div>
              </div>
              
              <div className="fallback-video">
                <video 
                  src={process.env.PUBLIC_URL + '/videos/property-showcase3.mp4'}
                  className="video-placeholder-img" 
                  muted
                  preload="metadata"
                />
                <div className="video-overlay">
                  <div className="video-title">Property Overview</div>
                  <button className="video-play-button" onClick={() => openVideoModal('/videos/property-showcase3.mp4')}>
                    <span className="play-icon">▶</span>
                    <span className="play-text">Watch Video 3</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <p className="video-description">Experience AeroIndustrialHub's premium facilities and strategic advantages through our virtual tours.</p>
        </div>
      </section>
      
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Us</h2>
          
          <div className="features-image-container">
            <img 
              src={process.env.PUBLIC_URL + '/images/why-choose-us.jpg'} 
              alt="AERO Industrial Hub Features" 
              className="centered-image"
            />
          </div>
          
          <div className="features-cards">
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fa fa-dollar-sign"></i>
              </div>
              <h3>Prime Opportunity</h3>
              <ul className="feature-list">
                <li>Unique 70 quality lots</li>
                <li>Strategic Location</li>
                <li>Open plan design versatile for showcasing, production, warehousing and storage</li>
                <li>Main road frontage and access</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fa fa-building"></i>
              </div>
              <h3>Versatile Spaces</h3>
              <ul className="feature-list">
                <li>Open plan layout provide flexibility</li>
                <li>Ease of loading and unloading</li>
                <li>Double-height ceilings with mezzanine level</li>
              </ul>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <i className="fa fa-map-marker-alt"></i>
              </div>
              <h3>Strategic Location</h3>
              <ul className="feature-list">
                <li>Direct access from Elite Highway, PLUS Highway, and LEKAS Highway</li>
                <li>Connectivity to major public amenities</li>
                <li>Excellent visibility with street frontage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      <section className="properties-preview">
        <div className="container">
          <h2 className="section-title">Featured Property</h2>
          <div className="featured-property-wrapper">
            <div className="property-card featured-property">
              <div className="property-card-image aero-hub-image"></div>
              <div className="property-info">
                <h3>AeroIndustrial Hub</h3>
                <p>State-of-the-art industrial complex with premium facilities and strategic location advantages. Our modern design provides flexibility for various business operations.</p>
                <Link to="/aero-industrial-hub" className="property-link">Explore Property</Link>
              </div>
            </div>
          </div>
          <div className="view-all">
            <Link to="/properties" className="btn btn-outline">View All Properties</Link>
          </div>
        </div>
      </section>
      
      {/* Property Types Section */}
      <section className="property-types-section">
        <div className="container">
          <h2 className="section-title">Our Property Types</h2>
          <p className="section-description">Explore our variety of industrial spaces designed to meet different business needs</p>
          
          {/* Master Plan Card */}
          <div className="property-type-card horizontal-card">
            <div className="property-type-image">
              <img src={process.env.PUBLIC_URL + '/images/master.jpg'} alt="Master Plan" />
            </div>
            <div className="property-type-info">
              <h3>Master Plan</h3>
              <p>Complete overview of the Aero Industrial Hub development with all available lots and facilities layout.</p>
              <p>The master plan shows the strategic design of our industrial complex, optimized for efficiency and growth potential.</p>
              <Link to="/aero-industrial-hub" className="btn btn-outline">View Details</Link>
            </div>
          </div>
          
          {/* Type A Card */}
          <div className="property-type-card horizontal-card">
            <div className="property-type-image">
              <img src={process.env.PUBLIC_URL + '/images/A.jpg'} alt="Type A Industrial Space" />
            </div>
            <div className="property-type-info">
              <h3>Type A</h3>
              <p>Premium industrial spaces designed for mid-to-large scale manufacturing and warehousing operations.</p>
              <p>Features include high ceilings, reinforced floors, and multiple loading bays suitable for heavy machinery and large inventories.</p>
              <Link to="/aero-industrial-hub" className="btn btn-outline">View Details</Link>
            </div>
          </div>
          
          {/* Type B Card */}
          <div className="property-type-card horizontal-card">
            <div className="property-type-image">
              <img src={process.env.PUBLIC_URL + '/images/B.jpg'} alt="Type B Industrial Space" />
            </div>
            <div className="property-type-info">
              <h3>Type B</h3>
              <p>Versatile industrial units perfect for small to medium businesses with specialized production needs.</p>
              <p>Offers flexible space configuration, dedicated office areas, and efficient loading facilities, ideal for growing enterprises.</p>
              <Link to="/aero-industrial-hub" className="btn btn-outline">View Details</Link>
            </div>
          </div>
          
          {/* Type C Card */}
          <div className="property-type-card horizontal-card">
            <div className="property-type-image">
              <img src={process.env.PUBLIC_URL + '/images/C.jpg'} alt="Type C Industrial Space" />
            </div>
            <div className="property-type-info">
              <h3>Type C</h3>
              <p>Compact industrial spaces perfect for startups, small manufacturing operations, or storage needs.</p>
              <p>These units offer excellent value with essential industrial features and strategic location benefits.</p>
              <Link to="/aero-industrial-hub" className="btn btn-outline">View Details</Link>
            </div>
          </div>
        </div>
      </section>
      
      <section className="location-section">
        <div className="container">
          <h2 className="section-title">Our Location</h2>
          
          {/* Location maps row layout */}
          <div className="location-maps-row">
            {/* Location map image */}
            <div className="location-image-container">
              <img 
                src={process.env.PUBLIC_URL + '/images/Location-map.jpg'} 
                alt="AeroIndustrial Hub Location Map" 
                className="location-map-image"
              />
            </div>
            
            {/* Google Maps iframe */}
            <div className="map-container">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7969.719236972974!2d101.79978229369677!3d2.856867238479122!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cdc5e7be70e8ef%3A0x16bcb6468b19c38f!2sNilai%203%20Wholesale%20Centre%2C%2071800%20Nilai%2C%20Negeri%20Sembilan!5e0!3m2!1sen!2smy!4v1740197655155!5m2!1sen!2smy" 
                width="800" 
                height="450" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="AeroIndustrial Hub Location"
              ></iframe>
            </div>
          </div>
          
          {/* Additional map images */}
          <div className="additional-maps">
            <div className="additional-map-container">
              <img 
                src={process.env.PUBLIC_URL + '/images/map1.jpg'} 
                alt="AeroIndustrial Hub Map 1" 
                className="additional-map-image"
              />
            </div>
            <div className="additional-map-container">
              <img 
                src={process.env.PUBLIC_URL + '/images/map2.jpg'} 
                alt="AeroIndustrial Hub Map 2" 
                className="additional-map-image"
              />
            </div>
          </div>
          
          <div className="location-info">
            <p>Visit us at Nilai 3 Wholesale Centre, 71800 Nilai, Negeri Sembilan, Malaysia</p>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="contact-section">
        <div className="container">
          <h2 className="section-title">Contact Us</h2>
          
          <div className="contact-content">
            <div className="contact-info">
              {/* <h3>Get In Touch</h3> */}
              <p>We're here to help with any questions about our industrial properties.</p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <i className="fa fa-envelope"></i>
                  <p>Email: <a href="mailto:aeroindustrialhub@gmail.com">aeroindustrialhub@gmail.com</a></p>
                </div>
                
                <div className="contact-item">
                  <i className="fa fa-phone"></i>
                  <p>Phone: <a href="tel:+60162820967">+60 16-282 0967</a></p>
                </div>
                
                {/* <div className="contact-item">
                  <i className="fa fa-phone"></i>
                  <p>Phone: <a href="tel:+60122398446">+60 12-239 8446</a></p>
                </div> */}
                
                <div className="contact-item">
                  <i className="fa fa-map-marker-alt"></i>
                  <p>Address: Nilai 3 Wholesale Centre, 71800 Nilai, Negeri Sembilan, Malaysia</p>
                </div>

                <div className="contact-item">
                  <FaWhatsapp className="whatsapp-icon" />
                  <p>
                    WhatsApp Us: 
                    <button onClick={openWhatsApp} className="whatsapp-button">
                      Message Now
                    </button>
                  </p>
                </div>

                <div className="contact-item">
                  <FaEnvelope className="email-icon" />
                  <p>
                    Contact Form: 
                    <button onClick={() => setShowContactModal(true)} className="contact-button">
                      Contact Us
                    </button>
                  </p>
                </div>
              </div>
            </div>
            
            {/* <div className="contact-image">
              <img 
                src={process.env.PUBLIC_URL + '/images/contact.jpg'} 
                alt="Contact AeroIndustrial Hub" 
                className="contact-photo"
              />
            </div> */}
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default Landing;
